import {useAuth} from "../../../providers/AuthProvider";
import {useProject} from "../../../providers/ClientProjectProvider";
import React, {useMemo, useState} from "react";
import {Button, Radio, Space, Upload} from "antd";
import {useIsMutating} from "@tanstack/react-query";
import SpinLoader from "../../../components/SpinLoader";
import Project from "../../../models/Project";

const SOWApprovalForm = (props) => {

    const {auth} = useAuth();

    const {
        project,
        isLoading,
        isProjectUpdating,
        setClientLeadUpdatesForProject,
        deleteProjectContract,
    } = useProject();

    const isOthersMutating = useIsMutating([]);

    const isMutating = useIsMutating(['updateProjectSOWSettings']);

    const clientSetupForTemplate = project.client_accepts_standard_time_materials_contract;
    const templateInvalid = project.issue_with_standard_contract_template;

    const [fileList, setFileList] = useState(() => {
        if (project.latestContract?.()) {
            let {
                type,
                name,
                ...file
            } = project.latestContract();

            if (project.latestContract().isTypeGeneratedContract()) {
                name = 'Auto-Generated Contract'
            }
            return [{name, url: project.latestContract().path, ...file}]
        }
        return []
    })

    const [contractModel, setContractModel] = useState({
        contract: null,
        is_active: false,
        client_contract_management_setting: project.client_contract_management_setting,
        upload_enabled: (clientSetupForTemplate && !templateInvalid) ? undefined : true,
    });

    const isSOWApprovalValid = useMemo(() => {
        if (!contractModel.client_contract_management_setting) {
            return false;
        }
        if (contractModel.client_contract_management_setting === Project.ContractSettings.NoSow) {
            return true;
        }
        return fileList.length > 0
    }, [
        contractModel?.upload_enabled,
        fileList?.length,
        contractModel?.client_contract_management_setting,
        clientSetupForTemplate,
        templateInvalid
    ])

    const [isGenerating, setIsGenerating] = useState(false);

    const isSOWApprovalCompleted = useMemo(() => {
        if (!project?.client_contract_management_setting) {
            return false;
        }
        return project?.client_contract_management_setting === Project.ContractSettings.NoSow || (
            project.is_client_contract_available && project.hasAutoGeneratedContract()
        ) || project.hasUploadedManualContract();
    }, [isProjectUpdating, project?.documents, isMutating, isOthersMutating]);

    const totalRolesFilled = TB.filter(project.positions, pos => pos.hasBeenFilled()).length;

    const resetContractModel = async (updatedProject) => {
        if (!updatedProject) return;

        await setContractModel({
            client_contract_management_setting: updatedProject.client_contract_management_setting,
        })

        if (updatedProject.latestContract?.()) {
            let {
                type,
                name,
                ...file
            } = updatedProject.latestContract();

            if (updatedProject.latestContract().isTypeGeneratedContract()) {
                name = 'Auto-Generated Contract'
            }
            await setFileList([{name, url: updatedProject.latestContract().path, ...file}])
        } else {
            await setFileList([])
        }
    }
    const updateSOWApprovalSettings = async (updateProps) => {
        let updatedProject = await setClientLeadUpdatesForProject(updateProps);

        console.log('updateSOWApprovalSettings', updatedProject)
        await resetContractModel(updatedProject);
        return updatedProject;
    }

    const resetSOWApproval = async () => {
        let confirm;
        if (project.client_contract_management_setting || project.latestContract()) {
            confirm = await TB.alert("alert", project.latestContract() ? "Delete Contract & Reset SOW Approval?" : "Reset SOW Approval?",
                `The SOW Approval settings will be reset${project.latestContract() ? ' and the existing contract will be deleted.' : ''}. Are you sure you want to continue?`, [
                    {title: 'Yes, Continue'},
                    {title: 'Cancel'}
                ]);
            if (confirm) {
                let updatedProject = await deleteProjectContract();
                if (updatedProject) {
                    TB.toast("SOW Approval Settings Reset");
                    return resetContractModel(updatedProject)
                }
            }
        }
    }
    const confirmNoSOW = async () => {
        let confirm = await TB.alert("warning", "Confirm",
            "You have selected to there is no SOW for this project or is not available at this time. " +
            "Are you sure you want to continue?",
            [{title: "Yes, Continue"}, {title: "Cancel"}]
        )

        if (confirm) {
            return updateSOWApprovalSettings({
                client_contract_management_setting: Project.ContractSettings.NoSow,
            })
        }
    }

    const confirmAutoGenerateOnline = async () => {
        let confirm = await TB.alert("warning", "Generate Contract & Make Available",
            "You have selected to auto-generate the SOW and make it available for the client to review. " +
            "Make sure you have finalized all position dates and confirmed candidate rates/hours. " +
            "The project can't be made active until the Client has reviewed and approved the SOW. " +
            "Are you sure you want to continue?",
            [{title: "Yes, Continue"}, {title: "Cancel"}]
        )

        if (confirm) {
            return updateSOWApprovalSettings({
                client_contract_management_setting: Project.ContractSettings.ApprovedOnline,
            })
        }
    }

    const confirmUploadOnline = async (file) => {
        let confirm = await TB.alert("warning", "Upload Contract & Make Available",
            "You have selected to upload the SOW and make it available for the client to review. " +
            "The project can't be made active until the Client has reviewed and approved the SOW. " +
            "Are you sure you want to continue?",
            [{title: "Yes, Continue"}, {title: "Cancel"}]
        )

        if (confirm) {
            return updateSOWApprovalSettings({
                client_contract_management_setting: Project.ContractSettings.ApprovedOnline,
                contract: file
            })
        }
    }

    const confirmUploadOffline = async (file) => {
        let confirm = await TB.alert("warning", "Upload Offline Contract",
            "You have selected to handle the SOW offline with the client. " +
            "Are you sure you want to continue?",
            [{title: "Yes, Continue"}, {title: "Cancel"}]
        )

        if (confirm) {
            return updateSOWApprovalSettings({
                client_contract_management_setting: Project.ContractSettings.ApprovedOffline,
                contract: file
            })
        }
    }

    if (isLoading || !project || !auth || !auth.canManageClients()) {
        return null;
    }

    return (
        <div
            className="bg-slate-100 min-w-[315px] flex-1 relative px-4 py-3.5 flex flex-col items-stretch justify-start gap-4 min-h-40">
            <div className="absolute left-0 right-0 flex flex-col items-center top-[-12px]">
                <i className={TB.mapClass("fas text-[24px]", {
                    "text-success fa-check-circle": isSOWApprovalCompleted,
                    "text-danger fa-times-circle": !isSOWApprovalCompleted
                })}/>

            </div>
            <p className="text-lg font-medium text-center">
                SOW Approval
            </p>
            {(project.hasRequestedSOW() && !project.isAwaitingKickoff() && (totalRolesFilled === project.positions?.length)) ? (
                <SpinLoader isLoading={isMutating > 0}>
                    <div>
                        <Radio.Group
                            onChange={async (e) => {

                                await setContractModel({
                                    ...contractModel,
                                    client_contract_management_setting: e.target.value
                                })

                                if (e.target.value === Project.ContractSettings.NoSow) {
                                    let didConfirm = await confirmNoSOW();

                                    if (!didConfirm) {

                                        return setContractModel({
                                            ...contractModel,
                                            client_contract_management_setting: null
                                        })
                                    }
                                }

                            }}
                            value={contractModel.client_contract_management_setting}>
                            <Space direction="vertical"
                                   style={TB.find(fileList, ['name', 'Auto-Generated Contract']) ? {} : {maxWidth: 210}}
                                   className="flex-grow relative">
                                <Radio value={Project.ContractSettings.ApprovedOnline}
                                       disabled={project.client_contract_management_setting && project.client_contract_management_setting !== Project.ContractSettings.ApprovedOnline}>
                                    Approved Through Platform
                                </Radio>
                                <div className="flex flex-row justify-between pl-6 pr-4 gap-3 mt-2 mb-4">
                                    <div
                                        style={TB.find(fileList, ['name', 'Auto-Generated Contract']) ? {} : {maxWidth: 120}}
                                        className="flex-1"
                                    >
                                        <Upload
                                            onRemove={async (file) => {
                                                return resetSOWApproval()
                                            }}
                                            beforeUpload={file => {
                                                confirmUploadOnline(file)
                                                return false
                                            }}
                                            fileList={(contractModel.client_contract_management_setting === Project.ContractSettings.ApprovedOnline || TB.find(fileList || [], ['name', 'Auto-Generated Contract'])) ? fileList : []}
                                            disabled={contractModel.client_contract_management_setting !== Project.ContractSettings.ApprovedOnline}
                                        >
                                            {((contractModel.client_contract_management_setting !== Project.ContractSettings.ApprovedOnline && !TB.find(fileList || [], ['name', 'Auto-Generated Contract'])) || fileList.length === 0) && (
                                                <Button
                                                    type="default"
                                                    disabled={isProjectUpdating || contractModel.client_contract_management_setting !== Project.ContractSettings.ApprovedOnline}
                                                    onClick={() => true}
                                                    className="flex-1 min-w-[115.5px] text-xs">
                                                    <i className="mr-1.5 fas fa-upload"/>
                                                    <span>Upload</span>
                                                </Button>
                                            )}
                                        </Upload>
                                    </div>

                                    {!project.latestContract()?.isTypeGeneratedContract?.() && (
                                        <Button
                                            type="primary"
                                            loading={isGenerating}
                                            disabled={isProjectUpdating || !clientSetupForTemplate || templateInvalid || contractModel.client_contract_management_setting !== Project.ContractSettings.ApprovedOnline}
                                            onClick={async () => {
                                                // await setIsGenerating(true);
                                                //
                                                // let result = await project.approveSOWContractDetails();
                                                //
                                                // if (result && result.latestContract?.()) {
                                                //     let {
                                                //         type,
                                                //         name,
                                                //         ...file
                                                //     } = result.latestContract();
                                                //
                                                //     await setFileList([{
                                                //         name: 'Auto-Generated Contract',
                                                //         url: result.latestContract().path, ...file
                                                //     }])
                                                // }
                                                //
                                                // await TB.wait(500);
                                                //
                                                // setIsGenerating(false)
                                                return confirmAutoGenerateOnline()
                                            }}
                                            className="flex-1 text-xs">
                                            Generate
                                        </Button>
                                    )}
                                </div>
                                <Radio value={Project.ContractSettings.ApprovedOffline}
                                       disabled={project.client_contract_management_setting && project.client_contract_management_setting !== Project.ContractSettings.ApprovedOffline}>
                                    Approved Off Platform
                                </Radio>
                                <div className="flex flex-row justify-between pl-6 pr-4 gap-3 mt-2 mb-4">
                                    <div style={{maxWidth: 280}} className="flex-1">
                                        <Upload
                                            onRemove={async (file) => {
                                                return resetSOWApproval()
                                            }}
                                            beforeUpload={file => {
                                                confirmUploadOffline(file)
                                                return false
                                            }}
                                            fileList={contractModel.client_contract_management_setting === Project.ContractSettings.ApprovedOffline ? fileList : []}
                                            disabled={contractModel.client_contract_management_setting !== Project.ContractSettings.ApprovedOffline}
                                        >
                                            {(contractModel.client_contract_management_setting !== Project.ContractSettings.ApprovedOffline || fileList.length === 0) && (
                                                <Button
                                                    type="default"
                                                    disabled={isProjectUpdating || contractModel.client_contract_management_setting !== Project.ContractSettings.ApprovedOffline}
                                                    onClick={() => true}
                                                    className="flex-1 min-w-[115.5px] text-xs">
                                                    <i className="mr-1.5 fas fa-upload"/>
                                                    <span>Upload</span>
                                                </Button>
                                            )}
                                        </Upload>
                                    </div>
                                    <div className="flex-1"/>
                                </div>
                                <Radio value={Project.ContractSettings.NoSow}
                                       disabled={project.client_contract_management_setting && project.client_contract_management_setting !== Project.ContractSettings.NoSow}>
                                    No SoW
                                </Radio>
                            </Space>
                        </Radio.Group>

                    </div>
                    <div className="mt-3 flex flex-row justify-end">
                        {project.client_contract_management_setting && (
                            <Button
                                type="primary"
                                disabled={isProjectUpdating}
                                onClick={async () => {
                                    return resetSOWApproval()
                                }}
                                className="min-w-[115.5px] text-xs">
                                Reset SOW Settings
                            </Button>
                        )}
                    </div>
                </SpinLoader>
            ) : (
                <div className="flex-1 flex flex-col items-center justify-center pb-5">
                    <p className="text-center">

                        {totalRolesFilled !== project.positions.length && (
                            <span>SOW Approval is disabled until all positions have been filled</span>
                        )}
                        {totalRolesFilled === project.positions.length && !project.hasRequestedSOW() && project.canRequestSOW() && (
                            <span>SOW Approval is disabled until Client has requested an SOW</span>
                        )}
                        {project.hasRequestedSOW() && project.isAwaitingKickoff() && (
                            <span>Client approved the SOW and the project is awaiting kickoff.</span>
                        )}
                    </p>
                </div>
            )}

        </div>
    )
}


export default SOWApprovalForm;